import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import propTypes from 'prop-types';

import { Calendar } from '@heroui/react';
import convertDatetoCalenderDate from '@components/IRIS/utility/convertDateToCalender';
import { CloseIcon } from '@components/IRIS/activityTab/icons/icons';
import { DateIcon } from '@components/userDocuments/editDocument/editJournal/toolset/assets/icons';
import PlaceHolder from '../../../../../../UI/Editor/EditSpace/EditorPane/RenderContent/placeholder';
import { toDates } from '../../../../../../../../../constants/resumeNodes';
import { isSafari } from '../../../../../../../../../utility/browsers';
import breakpointsMinWidth from '../../../../../../../../../styling/breakpointsMinWidth';

const returnDateStyle = ({ isFocused }) => {
  if (isFocused) {
    return css`
      border: 1px solid rgba(133, 16, 223, 1);
      box-sizing: border-box;
      border-radius: 4px;
    `;
  }
  return null;
};

const FromWrapper = styled.div`
  border: 1px solid #e5e5e5;
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 10px;
  position: relative;
  caret-color: transparent !important;
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  border-radius: 4px;
  font-size: 12px;
  line-height: 16px;
  color: #333d47;
  pointer-events: auto;
  cursor: pointer;

  ${(props) => returnDateStyle(props)}

  ${(props) => {
    const { dateType } = props;

    if (dateType === 'From') {
      return css`
        grid-column: 3/4;
        margin-right: 8px;
      `;
    }

    if (dateType === 'To') {
      return css`
        grid-column: 4/-1;
        margin-left: 8px;
      `;
    }

    return null;
  }}

  @media ${breakpointsMinWidth.mobileMicroLg} {
    padding: 0 16px;
  }

  @media ${breakpointsMinWidth.tabletSm} {
    height: 36px;
    padding: 0 9px;
  }

  @media ${breakpointsMinWidth.desktopSm} {
    padding: 0 10px;
  }

  @media ${breakpointsMinWidth.desktopMd} {
    height: 40px;
    padding: 0 14px;
    font-size: 14px;
  }
`;

const Picker = styled.span`
  pointer-events: auto;
  cursor: pointer;
  width: 100%;
  user-select: none;

  .react-datepicker {
    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    @media ${breakpointsMinWidth.desktopMd} {
      font-size: 14px;
    }
  }
  .react-datepicker-popper[data-placement='bottom-end']
    .react-datepicker__triangle,
  .react-datepicker-popper[data-placement='top-end']
    .react-datepicker__triangle {
    left: auto;
    right: 28px;
  }
  .react-datepicker-wrapper {
    width: inherit;
    .react-datepicker__input-container {
      vertical-align: middle;
    }
  }

  left: 10px;
  @media ${breakpointsMinWidth.mobileMicroLg} {
    left: 12px;
  }
  @media ${breakpointsMinWidth.tabletSm} {
    left: 11px;
  }
  @media ${breakpointsMinWidth.tabletMd} {
    left: 8px;
  }
  @media ${breakpointsMinWidth.tabletLg} {
    left: 10px;
  }
  @media ${breakpointsMinWidth.desktopMd} {
    left: 14px;
  }
`;

const PlaceHolder2 = styled(PlaceHolder)`
  ${(props) => {
    const { dateInput, isCurrent } = props;
    if (!dateInput && !isCurrent) {
      return css`
        top: 50%;
      `;
    }
    return null;
  }}
`;

class DateComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showCalendar: false,
      showCalendarHeader: false,
    };

    this.dateRef = React.createRef();
    this.closeRef = React.createRef();
  }

  componentDidMount = () => {
    const { setDateRef } = this.props;
    const {
      nodeProps: { node },
    } = this.props;

    document.addEventListener('click', this.handleClick);
    document.addEventListener('click', this.handleClickOutside);

    setDateRef(node.key, this.dateRef);
  };

  handleClick = (e) => {
    const {
      nodeProps: { node, editor },
    } = this.props;

    if (this.dateRef?.current && this.dateRef?.current?.contains(e.target)) {
      if (
        this.closeRef?.current &&
        this.closeRef?.current?.contains(e.target)
      ) {
        this.updateDate(null);
        return;
      }
      const { key: nodeKey } = node;
      const { current } = this.dateRef;
      const {
        value: { focusBlock },
      } = editor;
      if (node && focusBlock && nodeKey === focusBlock.key) {
        if (current) {
          this.setState({ showCalendar: true });
          editor.moveToStartOfNode(node).focus();
        }
      }
    }
  };

  handleClickOutside = (e) => {
    if (this.dateRef.current && !this.dateRef.current.contains(e.target)) {
      this.setState({ showCalendar: false, showCalendarHeader: false });
    }
  };

  componentWillUnmount = () => {
    document.removeEventListener('click', this.handleClick);
    document.removeEventListener('click', this.handleClickOutside);
  };

  updateDate = (value) => {
    const {
      nodeProps: { editor, node },
    } = this.props;

    const {
      value: { document },
    } = editor;

    const previousBlock = document.getPreviousBlock(node.key);

    if (previousBlock) {
      editor.moveToStartOfNode(previousBlock).focus();
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const formattedDate = value ? value.toDate(timezone) : null;

    editor.moveToStartOfNode(node).setNodeByKey(node.key, {
      data: {
        dateInput: formattedDate,
        current: false,
      },
    });
    this.setState({ showCalendar: false });
  };

  onClear = () => {
    const {
      nodeProps: { editor, node },
    } = this.props;
    editor
      .moveToStartOfNode(node)
      .setNodeByKey(node.key, {
        data: { dateInput: null },
      })
      .focus();
  };

  setCurrent = () => {
    const {
      nodeProps: { editor, node },
    } = this.props;

    editor
      .moveToStartOfNode(node)
      .setNodeByKey(node.key, {
        data: {
          dateInput: null,
          current: true,
        },
      })
      .focus();
  };

  isToDate = (type) => Object.values(toDates).includes(type);

  formatDateToDDMMYYYY(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  render() {
    const {
      nodeProps: { node, attributes, children, isFocused },
      placeHolder,
    } = this.props;

    const { showCalendar, showCalendarHeader } = this.state;

    const { text, data, type } = node;
    const nodeDateInput = data.get('dateInput');
    const dateInput = nodeDateInput ? new Date(nodeDateInput) : null;
    const isCurrent = data.get('current');
    const renderDate = this.formatDateToDDMMYYYY(dateInput);

    const calendarDate = dateInput
      ? convertDatetoCalenderDate(dateInput)
      : null;

    if (this.dateRef && this.dateRef.current) {
      this.dateRef.current.state = {};
      this.dateRef.current.state.open = showCalendar;
      this.dateRef.current.setOpen = (value) => {
        this.setState({ showCalendar: value });
      };
    }

    return (
      <FromWrapper
        {...attributes}
        dateType={placeHolder}
        nodeType={type}
        id="from"
        isFocused={isFocused}
        isSafari={isSafari}
        className="formWrapper"
        ref={this.dateRef}
      >
        <Picker spellCheck={false} data-testid={`${type}_date_picker`}>
          <div className="flex justify-between">
            <div>{dateInput && renderDate}</div>
            {dateInput ? (
              <div ref={this.closeRef}>
                <CloseIcon />
              </div>
            ) : (
              <DateIcon width="12px" height="12px" />
            )}
          </div>
          <Calendar
            className={`absolute z-50 top-[40px] right-0 max-w-[unset] ${showCalendar ? 'block' : 'hidden'}`}
            value={calendarDate}
            onChange={this.updateDate}
            aria-label="Date (Show Month and Year Picker)"
            showMonthAndYearPickers
            isHeaderExpanded={showCalendarHeader}
            onHeaderExpandedChange={(val) =>
              this.setState({ showCalendarHeader: val })
            }
          />
        </Picker>
        {children}
        <PlaceHolder2
          contentEditable={false}
          nodeText={text}
          dateInput={dateInput}
          isCurrent={isCurrent}
          isFocused={isFocused}
        >
          {placeHolder}
        </PlaceHolder2>
      </FromWrapper>
    );
  }
}

DateComponent.propTypes = {
  nodeProps: propTypes.objectOf(propTypes.object).isRequired,
  attributes: propTypes.objectOf(propTypes.string).isRequired,
  value: propTypes.objectOf(propTypes.string).isRequired,
  onClick: propTypes.func.isRequired,
  dateType: propTypes.string.isRequired,
  placeHolder: propTypes.string.isRequired,
};

export default DateComponent;
